import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, Shield, Package,
  Truck, FolderKanban, Users, Store, Clock, Calendar, CheckCircle,
  XCircle, AlertCircle, ChevronRight, RefreshCw, Bell, Target,
  ArrowUpRight, ArrowDownRight, Activity, Loader2, Eye, Zap,
  Building2, FileText, Award, BarChart3, ShoppingCart, Briefcase,
  Lightbulb, MessageSquare, ListChecks, CreditCard, Wallet, Hourglass, Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import MDQuickApprovalsWidget from '../components/widgets/MDQuickApprovalsWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CEODashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  
  // Data states
  const [businessHealth, setBusinessHealth] = useState(null);
  const [storesControl, setStoresControl] = useState(null);
  const [warehouseCommand, setWarehouseCommand] = useState(null);
  const [projectsStatus, setProjectsStatus] = useState(null);
  const [criticalAlerts, setCriticalAlerts] = useState(null);
  const [myTasks, setMyTasks] = useState(null);
  const [calendarReminders, setCalendarReminders] = useState(null);
  const [weeklyInsights, setWeeklyInsights] = useState(null);
  
  // Phase 5 Warehouse Financial Data
  const [warehouseSummary, setWarehouseSummary] = useState(null);
  const [warehouseBottlenecks, setWarehouseBottlenecks] = useState(null);
  const [agedReceivables, setAgedReceivables] = useState(null);
  const [agedPayables, setAgedPayables] = useState(null);
  const [warehouseTrends, setWarehouseTrends] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const fetchData = useCallback(async () => {
    try {
      const endpoints = [
        { key: 'businessHealth', url: '/api/ceo-dashboard/business-health', setter: setBusinessHealth },
        { key: 'storesControl', url: '/api/ceo-dashboard/stores-control', setter: setStoresControl },
        { key: 'warehouseCommand', url: '/api/ceo-dashboard/warehouse-command', setter: setWarehouseCommand },
        { key: 'projectsStatus', url: '/api/ceo-dashboard/projects-status', setter: setProjectsStatus },
        { key: 'criticalAlerts', url: '/api/ceo-dashboard/critical-alerts', setter: setCriticalAlerts },
        { key: 'myTasks', url: '/api/ceo-dashboard/my-tasks', setter: setMyTasks },
        { key: 'calendarReminders', url: '/api/ceo-dashboard/calendar-reminders', setter: setCalendarReminders },
        { key: 'weeklyInsights', url: '/api/ceo-dashboard/weekly-insights', setter: setWeeklyInsights },
        // Phase 5 Warehouse Financial Endpoints
        { key: 'warehouseSummary', url: '/api/ceo-dashboard/summary', setter: setWarehouseSummary },
        { key: 'warehouseBottlenecks', url: '/api/ceo-dashboard/bottlenecks', setter: setWarehouseBottlenecks },
        { key: 'agedReceivables', url: '/api/ceo-dashboard/aged-receivables', setter: setAgedReceivables },
        { key: 'agedPayables', url: '/api/ceo-dashboard/aged-payables', setter: setAgedPayables },
        { key: 'warehouseTrends', url: '/api/ceo-dashboard/trends?days=30', setter: setWarehouseTrends },
      ];

      const results = await Promise.allSettled(
        endpoints.map(ep => 
          fetch(`${API_URL}${ep.url}`, { headers }).then(r => r.ok ? r.json() : null)
        )
      );

      endpoints.forEach((ep, idx) => {
        if (results[idx].status === 'fulfilled' && results[idx].value) {
          ep.setter(results[idx].value);
        }
      });
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Dashboard refreshed');
  };

  const formatCurrency = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`;
    return `$${num.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading Command Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ceo-dashboard">
      {/* ===== 1. BUSINESS HEALTH TOP STRIP ===== */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white px-4 md:px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: '#ffffff' }}>Operations Control</h1>
            <Badge className="bg-green-500/30 text-green-300 border-green-500/50">
              <Activity className="w-3 h-3 mr-1" /> Live
            </Badge>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="border-white/30 text-white hover:bg-white/10 bg-white/5"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Business Health Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Sales Today */}
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-xs text-gray-300">Sales Today</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(businessHealth?.sales?.today || 0)}</p>
            <span className={`text-xs flex items-center ${businessHealth?.sales?.trend?.startsWith('+') ? 'text-green-400' : 'text-red-400'}`}>
              {businessHealth?.sales?.trend?.startsWith('+') ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {businessHealth?.sales?.trend}
            </span>
          </div>

          {/* Sales Week */}
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-gray-300">Sales Week</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(businessHealth?.sales?.week || 0)}</p>
          </div>

          {/* Sales Month */}
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-purple-400" />
              <span className="text-xs text-gray-300">Sales Month</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(businessHealth?.sales?.month || 0)}</p>
          </div>

          {/* Purchase */}
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart className="w-4 h-4 text-orange-400" />
              <span className="text-xs text-gray-300">Purchase MTD</span>
            </div>
            <p className="text-xl font-bold text-white">{formatCurrency(businessHealth?.purchase?.month || 0)}</p>
          </div>

          {/* Sales vs Purchase Ratio */}
          <div className={`rounded-lg p-3 ${
            businessHealth?.ratio?.health === 'good' ? 'bg-green-500/30' :
            businessHealth?.ratio?.health === 'warning' ? 'bg-yellow-500/30' : 'bg-red-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <Target className="w-4 h-4 text-white" />
              <span className="text-xs text-gray-200">Sales/Purchase</span>
            </div>
            <p className="text-xl font-bold text-white">{businessHealth?.ratio?.month || 0}%</p>
            <span className="text-xs text-white font-medium">
              {businessHealth?.ratio?.health === 'good' ? 'Healthy' :
               businessHealth?.ratio?.health === 'warning' ? 'Watch' : 'Critical'}
            </span>
          </div>

          {/* Critical Alerts */}
          <div className={`rounded-lg p-3 ${
            (businessHealth?.critical_alerts?.total || 0) > 0 ? 'bg-red-500/40' : 'bg-green-500/30'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-white" />
              <span className="text-xs text-gray-200">Critical Alerts</span>
            </div>
            <p className="text-xl font-bold text-white">{businessHealth?.critical_alerts?.total || 0}</p>
            <button
              onClick={() => setActiveSection('alerts')}
              className="text-xs text-white underline hover:no-underline"
            >
              View All
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 lg:p-6">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
          {[
            { id: 'overview', label: 'Overview', icon: Eye },
            { id: 'stores', label: 'Stores', icon: Store },
            { id: 'warehouse', label: 'Warehouse', icon: Package },
            { id: 'financials', label: 'Financials', icon: Wallet, badge: (warehouseSummary?.pending_approvals?.total || 0) > 0 ? warehouseSummary.pending_approvals.total : null },
            { id: 'projects', label: 'Projects', icon: FolderKanban },
            { id: 'alerts', label: 'Alerts', icon: Bell, badge: criticalAlerts?.total },
            { id: 'tasks', label: 'My Tasks', icon: ListChecks, badge: myTasks?.summary?.overdue },
            { id: 'calendar', label: 'Calendar', icon: Calendar },
            { id: 'insights', label: 'Insights', icon: Lightbulb },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeSection === tab.id
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <Badge className="bg-red-500 text-white text-xs ml-1">{tab.badge}</Badge>
              )}
            </button>
          ))}
        </div>

        {/* ===== OVERVIEW SECTION ===== */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {/* Quick Action Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Stores Summary */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-blue-500" onClick={() => setActiveSection('stores')}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Stores</p>
                      <p className="text-2xl font-bold">{storesControl?.summary?.total_stores || 0}</p>
                      <p className="text-xs text-red-500 mt-1">
                        {storesControl?.summary?.stores_needing_attention || 0} need attention
                      </p>
                    </div>
                    <Store className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Warehouse Summary */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-orange-500" onClick={() => setActiveSection('warehouse')}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Warehouse</p>
                      <p className="text-2xl font-bold">{warehouseCommand?.deliveries?.on_time_pct || 0}%</p>
                      <p className="text-xs text-gray-500">On-time delivery</p>
                    </div>
                    <Package className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Projects Summary */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-purple-500" onClick={() => setActiveSection('projects')}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Projects</p>
                      <p className="text-2xl font-bold">{projectsStatus?.summary?.total || 0}</p>
                      <p className="text-xs text-red-500 mt-1">
                        {projectsStatus?.summary?.at_risk || 0} at risk
                      </p>
                    </div>
                    <FolderKanban className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>

              {/* Staff Summary */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-green-500" onClick={() => navigate('/admin/team-structure')}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm text-gray-500">Staff KPIs</p>
                      <p className="text-2xl font-bold text-green-600">
                        {storesControl?.summary?.top_performers_total || 0}
                      </p>
                      <p className="text-xs text-gray-500">Top performers</p>
                      <p className="text-xs text-red-500">
                        {storesControl?.summary?.low_performers_total || 0} low performers
                      </p>
                    </div>
                    <Users className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Approvals Widget for MD - Mobile Optimized */}
            <div className="lg:hidden">
              <MDQuickApprovalsWidget />
            </div>

            {/* Three Column Layout - Desktop Shows Quick Approvals */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Critical Alerts Preview */}
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Critical Alerts
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setActiveSection('alerts')}>
                      View All <ChevronRight className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {criticalAlerts?.alerts?.length > 0 ? (
                    <div className="space-y-2">
                      {criticalAlerts.alerts.slice(0, 4).map((alert, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-red-50 rounded-lg">
                          <AlertCircle className={`w-4 h-4 flex-shrink-0 ${
                            alert.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{alert.issue}</p>
                            <p className="text-xs text-gray-500">{alert.store} • {alert.responsible}</p>
                          </div>
                          <Badge className={
                            alert.severity === 'critical' ? 'bg-red-500' : 'bg-yellow-500'
                          }>{alert.days_pending}d</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-gray-500">No critical alerts</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* My Tasks Preview */}
              <Card className="border-l-4 border-l-indigo-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <ListChecks className="w-5 h-5 text-indigo-500" />
                      Tasks Assigned to You
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setActiveSection('tasks')}>
                      View All <ChevronRight className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {myTasks?.tasks?.length > 0 ? (
                    <div className="space-y-2">
                      {myTasks.tasks.slice(0, 4).map((task, idx) => (
                        <div key={idx} className={`flex items-center gap-3 p-2 rounded-lg ${
                          task.is_overdue ? 'bg-red-50' : 'bg-gray-50'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            task.priority === 'high' ? 'bg-red-500' :
                            task.priority === 'medium' ? 'bg-yellow-500' : 'bg-gray-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{task.title}</p>
                            <p className="text-xs text-gray-500">From: {task.assigned_by}</p>
                          </div>
                          {task.is_overdue && (
                            <Badge className="bg-red-500">Overdue</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                      <p className="text-gray-500">No pending tasks</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Approvals - Desktop Only */}
              <div className="hidden lg:block">
                <MDQuickApprovalsWidget />
              </div>
            </div>

            {/* Stores Grid Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Store Performance</span>
                  <Button variant="ghost" size="sm" onClick={() => setActiveSection('stores')}>
                    View Details <ChevronRight className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {storesControl?.stores?.slice(0, 6).map((store, idx) => (
                    <StoreCard key={idx} store={store} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== STORES SECTION ===== */}
        {activeSection === 'stores' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Store className="w-6 h-6 text-blue-500" />
                Stores Control Panel
              </h2>
              <div className="flex gap-2">
                <Badge className="bg-green-100 text-green-700">
                  {storesControl?.summary?.top_performers_total || 0} Top Performers
                </Badge>
                <Badge className="bg-red-100 text-red-700">
                  {storesControl?.summary?.low_performers_total || 0} Low Performers
                </Badge>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {storesControl?.stores?.map((store, idx) => (
                <StoreDetailCard key={idx} store={store} />
              ))}
            </div>
          </div>
        )}

        {/* ===== WAREHOUSE SECTION ===== */}
        {activeSection === 'warehouse' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Package className="w-6 h-6 text-orange-500" />
              Warehouse Command Panel
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Deliveries */}
              <Card className="border-t-4 border-t-blue-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Truck className="w-5 h-5" />
                    Deliveries
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">On-time %</span>
                    <span className={`text-2xl font-bold ${
                      warehouseCommand?.deliveries?.on_time_pct >= 90 ? 'text-green-600' : 'text-yellow-600'
                    }`}>{warehouseCommand?.deliveries?.on_time_pct}%</span>
                  </div>
                  <Progress value={warehouseCommand?.deliveries?.on_time_pct || 0} className="h-2" />
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold">{warehouseCommand?.deliveries?.total_today || 0}</p>
                      <p className="text-xs text-gray-500">Today</p>
                    </div>
                    <div className="text-center p-2 bg-gray-50 rounded">
                      <p className="text-lg font-bold">{warehouseCommand?.deliveries?.delays_count || 0}</p>
                      <p className="text-xs text-gray-500">Delays</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Purchasing Intelligence */}
              <Card className="border-t-4 border-t-purple-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Purchasing Intelligence
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="text-sm">Over-purchasing</span>
                    <Badge className="bg-yellow-500">{warehouseCommand?.purchasing_intelligence?.over_purchasing_alerts || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-sm">Under-stock</span>
                    <Badge className="bg-red-500">{warehouseCommand?.purchasing_intelligence?.under_stock_alerts || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">Dead Stock Value</span>
                    <span className="font-bold text-red-600">${warehouseCommand?.purchasing_intelligence?.dead_stock_value?.toLocaleString() || 0}</span>
                  </div>
                  <div className="pt-2">
                    <p className="text-sm text-gray-500 mb-1">Ordering Quality Score</p>
                    <div className="flex items-center gap-2">
                      <Progress value={warehouseCommand?.purchasing_intelligence?.ordering_quality_score || 0} className="h-3 flex-1" />
                      <span className="font-bold">{warehouseCommand?.purchasing_intelligence?.ordering_quality_score || 0}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Pipeline */}
              <Card className="border-t-4 border-t-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5" />
                    Pipeline Visibility
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="text-sm">In Transit (Containers)</span>
                    <span className="font-bold">{warehouseCommand?.pipeline?.in_transit_containers || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                    <span className="text-sm">At Warehouse</span>
                    <span className="font-bold">{warehouseCommand?.pipeline?.at_warehouse?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="text-sm">Allocated to Stores</span>
                    <span className="font-bold">{warehouseCommand?.pipeline?.allocated_to_stores?.toLocaleString() || 0}</span>
                  </div>
                  <div className="pt-2">
                    <Badge className={
                      warehouseCommand?.pipeline?.status === 'smooth' ? 'bg-green-500' :
                      warehouseCommand?.pipeline?.status === 'risk' ? 'bg-yellow-500' : 'bg-red-500'
                    }>
                      Pipeline: {warehouseCommand?.pipeline?.status?.toUpperCase() || 'UNKNOWN'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ===== FINANCIALS SECTION (Phase 5) ===== */}
        {activeSection === 'financials' && (
          <div className="space-y-6" data-testid="financials-section">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Wallet className="w-6 h-6 text-emerald-500" />
              Warehouse Financials & Bottlenecks
            </h2>

            {/* Key Metrics Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="border-t-4 border-t-green-500">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Receivables</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(agedReceivables?.total || 0)}</p>
                  <p className="text-xs text-gray-400">{warehouseSummary?.receivables?.count || 0} invoices</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-red-500">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Payables</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(agedPayables?.total || 0)}</p>
                  <p className="text-xs text-gray-400">{warehouseSummary?.payables?.count || 0} invoices</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-blue-500">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Stock Value</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(warehouseSummary?.stock_value || 0)}</p>
                  <p className="text-xs text-gray-400">{warehouseSummary?.products?.total || 0} products</p>
                </CardContent>
              </Card>
              <Card className={`border-t-4 ${(warehouseSummary?.pending_approvals?.total || 0) > 0 ? 'border-t-orange-500' : 'border-t-gray-300'}`}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Pending Approvals</p>
                  <p className={`text-2xl font-bold ${(warehouseSummary?.pending_approvals?.total || 0) > 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                    {warehouseSummary?.pending_approvals?.total || 0}
                  </p>
                  <p className="text-xs text-gray-400">awaiting action</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-purple-500">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Customers</p>
                  <p className="text-2xl font-bold text-purple-600">{warehouseSummary?.customers || 0}</p>
                  <p className="text-xs text-gray-400">active</p>
                </CardContent>
              </Card>
              <Card className="border-t-4 border-t-teal-500">
                <CardContent className="p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Low Stock</p>
                  <p className={`text-2xl font-bold ${(warehouseSummary?.products?.low_stock || 0) > 0 ? 'text-red-600' : 'text-teal-600'}`}>
                    {warehouseSummary?.products?.low_stock || 0}
                  </p>
                  <p className="text-xs text-gray-400">products</p>
                </CardContent>
              </Card>
            </div>

            {/* Bottlenecks Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Purchase Order Bottlenecks */}
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-orange-700">
                    <ShoppingCart className="w-5 h-5" />
                    Purchase Order Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">Draft POs</span>
                    <Badge variant="outline">{warehouseBottlenecks?.purchase_orders?.draft || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">Sent to Supplier</span>
                    <Badge variant="outline">{warehouseBottlenecks?.purchase_orders?.sent_to_supplier || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="text-sm">Partial Received</span>
                    <Badge className="bg-yellow-500">{warehouseBottlenecks?.purchase_orders?.partial_received || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span className="text-sm">Pending Invoice</span>
                    <Badge className="bg-orange-500">{warehouseBottlenecks?.purchase_orders?.pending_invoice || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-sm font-medium">Pending GTP Approval</span>
                    <Badge className="bg-red-500">{warehouseBottlenecks?.purchase_orders?.pending_gtp || 0}</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Sales Order Bottlenecks */}
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <Truck className="w-5 h-5" />
                    Sales Order Pipeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-sm font-medium">Pending Credit Approval</span>
                    <Badge className="bg-red-500">{warehouseBottlenecks?.sales_orders?.pending_credit_approval || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="text-sm">Confirmed</span>
                    <Badge variant="outline">{warehouseBottlenecks?.sales_orders?.confirmed || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                    <span className="text-sm">Ready for Dispatch</span>
                    <Badge className="bg-blue-500">{warehouseBottlenecks?.sales_orders?.ready_for_dispatch || 0}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                    <span className="text-sm">Dispatched (In Transit)</span>
                    <Badge className="bg-purple-500">{warehouseBottlenecks?.sales_orders?.dispatched || 0}</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Approval Queue */}
              <Card className="border-l-4 border-l-red-500">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <Hourglass className="w-5 h-5" />
                    Pending Approvals (MD)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-orange-50 rounded">
                    <span className="text-sm">GTP (Good To Pay)</span>
                    <Badge className={warehouseBottlenecks?.approvals?.gtp > 0 ? 'bg-orange-500' : 'bg-gray-300'}>
                      {warehouseBottlenecks?.approvals?.gtp || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-sm">Credit Over-Limit</span>
                    <Badge className={warehouseBottlenecks?.approvals?.credit_approval > 0 ? 'bg-red-500' : 'bg-gray-300'}>
                      {warehouseBottlenecks?.approvals?.credit_approval || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-yellow-50 rounded">
                    <span className="text-sm">Stock Adjustments</span>
                    <Badge className={warehouseBottlenecks?.approvals?.stock_adjustments > 0 ? 'bg-yellow-500' : 'bg-gray-300'}>
                      {warehouseBottlenecks?.approvals?.stock_adjustments || 0}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-purple-50 rounded">
                    <span className="text-sm">Credit Notes</span>
                    <Badge className={warehouseBottlenecks?.approvals?.credit_notes > 0 ? 'bg-purple-500' : 'bg-gray-300'}>
                      {warehouseBottlenecks?.approvals?.credit_notes || 0}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Aged Receivables & Payables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Aged Receivables */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    Aged Receivables (What Customers Owe Us)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['current', '1-30', '31-60', '61-90', '90+'].map(bucket => (
                      <div key={bucket} className="flex items-center gap-3">
                        <span className={`text-sm font-medium w-16 ${bucket === '90+' ? 'text-red-600' : ''}`}>
                          {bucket === 'current' ? 'Current' : `${bucket} days`}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-4 rounded-full ${
                              bucket === 'current' ? 'bg-green-500' :
                              bucket === '1-30' ? 'bg-blue-500' :
                              bucket === '31-60' ? 'bg-yellow-500' :
                              bucket === '61-90' ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{
                              width: agedReceivables?.total > 0
                                ? `${Math.min(100, (agedReceivables?.buckets?.[bucket]?.amount || 0) / agedReceivables.total * 100)}%`
                                : '0%'
                            }}
                          />
                        </div>
                        <span className="text-sm font-bold w-24 text-right">
                          {formatCurrency(agedReceivables?.buckets?.[bucket]?.amount || 0)}
                        </span>
                        <Badge variant="outline" className="w-12 justify-center">
                          {agedReceivables?.buckets?.[bucket]?.count || 0}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  {agedReceivables?.top_customers?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-gray-500 mb-2">Top Owing Customers</p>
                      <div className="space-y-1">
                        {agedReceivables.top_customers.slice(0, 5).map((c, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="truncate flex-1">{c.customer_name}</span>
                            <span className="font-medium text-green-600">{formatCurrency(c.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Aged Payables */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-red-600" />
                    Aged Payables (What We Owe Suppliers)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {['current', '1-30', '31-60', '61-90', '90+'].map(bucket => (
                      <div key={bucket} className="flex items-center gap-3">
                        <span className={`text-sm font-medium w-16 ${bucket === '90+' ? 'text-red-600' : ''}`}>
                          {bucket === 'current' ? 'Current' : `${bucket} days`}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                          <div
                            className={`h-4 rounded-full ${
                              bucket === 'current' ? 'bg-green-500' :
                              bucket === '1-30' ? 'bg-blue-500' :
                              bucket === '31-60' ? 'bg-yellow-500' :
                              bucket === '61-90' ? 'bg-orange-500' : 'bg-red-500'
                            }`}
                            style={{
                              width: agedPayables?.total > 0
                                ? `${Math.min(100, (agedPayables?.buckets?.[bucket]?.amount || 0) / agedPayables.total * 100)}%`
                                : '0%'
                            }}
                          />
                        </div>
                        <span className="text-sm font-bold w-24 text-right">
                          {formatCurrency(agedPayables?.buckets?.[bucket]?.amount || 0)}
                        </span>
                        <Badge variant="outline" className="w-12 justify-center">
                          {agedPayables?.buckets?.[bucket]?.count || 0}
                        </Badge>
                      </div>
                    ))}
                  </div>
                  {agedPayables?.top_suppliers?.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs font-medium text-gray-500 mb-2">Top Owed Suppliers</p>
                      <div className="space-y-1">
                        {agedPayables.top_suppliers.slice(0, 5).map((s, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="truncate flex-1">{s.supplier_name}</span>
                            <span className="font-medium text-red-600">{formatCurrency(s.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Oldest Pending Items */}
            {(warehouseBottlenecks?.oldest_pending?.gtp?.length > 0 ||
              warehouseBottlenecks?.oldest_pending?.credit_approval?.length > 0 ||
              warehouseBottlenecks?.oldest_pending?.adjustments?.length > 0 ||
              warehouseBottlenecks?.oldest_pending?.credit_notes?.length > 0) && (
              <Card className="bg-red-50 border-l-4 border-l-red-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Oldest Pending Items (Require Immediate Attention)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {warehouseBottlenecks?.oldest_pending?.gtp?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">GTP Approvals</p>
                        {warehouseBottlenecks.oldest_pending.gtp.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-white rounded mb-1">
                            <p className="font-medium truncate">{item.receiving_number}</p>
                            <p className="text-xs text-gray-500">{item.supplier_name} - {formatCurrency(item.invoice_total || 0)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {warehouseBottlenecks?.oldest_pending?.credit_approval?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Credit Approvals</p>
                        {warehouseBottlenecks.oldest_pending.credit_approval.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-white rounded mb-1">
                            <p className="font-medium truncate">{item.order_number}</p>
                            <p className="text-xs text-gray-500">{item.customer_name} - {formatCurrency(item.total_amount || 0)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {warehouseBottlenecks?.oldest_pending?.adjustments?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Stock Adjustments</p>
                        {warehouseBottlenecks.oldest_pending.adjustments.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-white rounded mb-1">
                            <p className="font-medium truncate">{item.adjustment_number}</p>
                            <p className="text-xs text-gray-500">{item.product_name} - Qty: {item.quantity}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {warehouseBottlenecks?.oldest_pending?.credit_notes?.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Credit Notes</p>
                        {warehouseBottlenecks.oldest_pending.credit_notes.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="text-sm p-2 bg-white rounded mb-1">
                            <p className="font-medium truncate">{item.credit_note_number}</p>
                            <p className="text-xs text-gray-500">{item.customer_name} - {formatCurrency(item.total_amount || 0)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sales vs Purchases Trends Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-indigo-600" />
                  Sales vs Purchases (Last 30 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {warehouseTrends?.sales?.length > 0 || warehouseTrends?.purchases?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Total Sales</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) || 0)}
                        </p>
                        <p className="text-xs text-gray-400">{warehouseTrends?.sales?.reduce((sum, d) => sum + d.count, 0) || 0} orders</p>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Total Purchases</p>
                        <p className="text-xl font-bold text-red-600">
                          {formatCurrency(warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0) || 0)}
                        </p>
                        <p className="text-xs text-gray-400">{warehouseTrends?.purchases?.reduce((sum, d) => sum + d.count, 0) || 0} POs</p>
                      </div>
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Net Position</p>
                        <p className={`text-xl font-bold ${
                          (warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) || 0) - 
                          (warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0) || 0) >= 0 
                            ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(
                            (warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) || 0) - 
                            (warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0) || 0)
                          )}
                        </p>
                        <p className="text-xs text-gray-400">Sales - Purchases</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <p className="text-xs text-gray-500">Margin %</p>
                        <p className={`text-xl font-bold ${
                          ((warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) || 0) / 
                           Math.max(1, warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0) || 1) * 100) >= 100 
                            ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0) > 0 
                            ? Math.round((warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) || 0) / 
                              (warehouseTrends?.purchases?.reduce((sum, d) => sum + d.total, 0)) * 100) 
                            : warehouseTrends?.sales?.reduce((sum, d) => sum + d.total, 0) > 0 ? '∞' : '0'}%
                        </p>
                        <p className="text-xs text-gray-400">Sales/Purchases</p>
                      </div>
                    </div>

                    {/* Bar Chart */}
                    <div className="relative h-64 border rounded-lg p-4 bg-gray-50">
                      <div className="flex h-full items-end gap-1">
                        {(() => {
                          // Combine dates from both sales and purchases
                          const allDates = new Set([
                            ...(warehouseTrends?.sales?.map(s => s.date) || []),
                            ...(warehouseTrends?.purchases?.map(p => p.date) || [])
                          ]);
                          const sortedDates = Array.from(allDates).sort();
                          
                          // Find max value for scaling
                          const maxValue = Math.max(
                            ...(warehouseTrends?.sales?.map(s => s.total) || [0]),
                            ...(warehouseTrends?.purchases?.map(p => p.total) || [0]),
                            1
                          );
                          
                          return sortedDates.map((date, idx) => {
                            const salesData = warehouseTrends?.sales?.find(s => s.date === date);
                            const purchasesData = warehouseTrends?.purchases?.find(p => p.date === date);
                            const salesHeight = salesData ? (salesData.total / maxValue * 100) : 0;
                            const purchasesHeight = purchasesData ? (purchasesData.total / maxValue * 100) : 0;
                            
                            return (
                              <div key={idx} className="flex-1 flex gap-0.5 items-end group relative">
                                {/* Sales Bar */}
                                <div 
                                  className="flex-1 bg-green-500 rounded-t transition-all hover:bg-green-600"
                                  style={{ height: `${Math.max(salesHeight, salesData ? 2 : 0)}%` }}
                                  title={`Sales: ${formatCurrency(salesData?.total || 0)}`}
                                />
                                {/* Purchases Bar */}
                                <div 
                                  className="flex-1 bg-red-400 rounded-t transition-all hover:bg-red-500"
                                  style={{ height: `${Math.max(purchasesHeight, purchasesData ? 2 : 0)}%` }}
                                  title={`Purchases: ${formatCurrency(purchasesData?.total || 0)}`}
                                />
                                {/* Tooltip on hover */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                                  <p className="font-medium">{date}</p>
                                  <p className="text-green-300">Sales: {formatCurrency(salesData?.total || 0)}</p>
                                  <p className="text-red-300">Purchases: {formatCurrency(purchasesData?.total || 0)}</p>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      {/* X-axis labels */}
                      <div className="absolute bottom-0 left-4 right-4 flex justify-between text-xs text-gray-400 mt-2">
                        <span>30 days ago</span>
                        <span>Today</span>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="flex justify-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded" />
                        <span>Sales (Delivered)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-400 rounded" />
                        <span>Purchases (GTP Approved)</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No transaction data in the last 30 days</p>
                    <p className="text-xs mt-1">Trends will appear once sales orders are delivered and purchases are approved</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== PROJECTS SECTION ===== */}
        {activeSection === 'projects' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FolderKanban className="w-6 h-6 text-purple-500" />
                Projects Dashboard
              </h2>
              <Button onClick={() => navigate('/admin/projects')}>
                Manage Projects <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Projects at Risk */}
            {projectsStatus?.at_risk?.length > 0 && (
              <Card className="border-l-4 border-l-red-500 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-700 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Projects at Risk ({projectsStatus.at_risk.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projectsStatus.at_risk.map((project, idx) => (
                      <ProjectCard key={idx} project={project} isRisk />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* All Projects */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projectsStatus?.projects?.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </div>
        )}

        {/* ===== ALERTS SECTION ===== */}
        {activeSection === 'alerts' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="w-6 h-6 text-red-500" />
              Critical Alerts Panel
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              {[
                { label: 'Compliance', count: criticalAlerts?.by_type?.compliance || 0, color: 'red' },
                { label: 'Expiry', count: criticalAlerts?.by_type?.expiry || 0, color: 'orange' },
                { label: 'Stock', count: criticalAlerts?.by_type?.stock || 0, color: 'yellow' },
                { label: 'Staff', count: criticalAlerts?.by_type?.staff || 0, color: 'purple' },
                { label: 'Projects', count: criticalAlerts?.by_type?.projects || 0, color: 'blue' },
              ].map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 text-center">
                    <p className={`text-3xl font-bold text-${item.color}-600`}>{item.count}</p>
                    <p className="text-sm text-gray-500">{item.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Issue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Store/Dept</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Responsible</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Days Pending</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {criticalAlerts?.alerts?.map((alert, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{alert.issue}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{alert.store}</td>
                        <td className="px-4 py-3 text-sm">{alert.responsible}</td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="outline">{alert.days_pending} days</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={
                            alert.severity === 'critical' ? 'bg-red-500' :
                            alert.severity === 'high' ? 'bg-orange-500' : 'bg-yellow-500'
                          }>{alert.severity}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== TASKS SECTION ===== */}
        {activeSection === 'tasks' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <ListChecks className="w-6 h-6 text-indigo-500" />
              Tasks Assigned to You
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-red-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{myTasks?.summary?.overdue || 0}</p>
                  <p className="text-sm text-gray-500">Overdue</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-orange-600">{myTasks?.summary?.high_priority || 0}</p>
                  <p className="text-sm text-gray-500">High Priority</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{myTasks?.summary?.due_today || 0}</p>
                  <p className="text-sm text-gray-500">Due Today</p>
                </CardContent>
              </Card>
              <Card className="bg-gray-50">
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold">{myTasks?.summary?.total || 0}</p>
                  <p className="text-sm text-gray-500">Total Tasks</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="divide-y">
                {myTasks?.tasks?.map((task, idx) => (
                  <div key={idx} className={`py-4 flex items-center gap-4 ${task.is_overdue ? 'bg-red-50 -mx-4 px-4' : ''}`}>
                    <div className={`w-3 h-3 rounded-full ${
                      task.priority === 'high' ? 'bg-red-500' :
                      task.priority === 'medium' ? 'bg-yellow-500' : 'bg-gray-300'
                    }`} />
                    <div className="flex-1">
                      <p className="font-medium">{task.title}</p>
                      <p className="text-sm text-gray-500">Assigned by: {task.assigned_by}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm">{task.due_date?.split('T')[0]}</p>
                      {task.is_overdue && <Badge className="bg-red-500">Overdue</Badge>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== CALENDAR SECTION ===== */}
        {activeSection === 'calendar' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6 text-teal-500" />
              Calendar & Reminders
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card><CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{calendarReminders?.categories?.leases || 0}</p>
                <p className="text-sm text-gray-500">Lease Renewals</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-orange-600">{calendarReminders?.categories?.licenses || 0}</p>
                <p className="text-sm text-gray-500">Licenses</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{calendarReminders?.categories?.equipment || 0}</p>
                <p className="text-sm text-gray-500">Equipment Service</p>
              </CardContent></Card>
              <Card><CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-purple-600">{calendarReminders?.categories?.meetings || 0}</p>
                <p className="text-sm text-gray-500">Meetings</p>
              </CardContent></Card>
            </div>

            <Card>
              <CardContent className="divide-y">
                {calendarReminders?.reminders?.map((item, idx) => (
                  <div key={idx} className="py-4 flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${
                      item.type === 'license' ? 'bg-orange-100' : 'bg-purple-100'
                    }`}>
                      {item.type === 'meeting' ? <Calendar className="w-5 h-5 text-purple-600" /> : <FileText className="w-5 h-5 text-orange-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.title}</p>
                      <p className="text-sm text-gray-500">{item.category}</p>
                    </div>
                    <Badge variant="outline">{item.date?.split('T')[0]}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ===== INSIGHTS SECTION ===== */}
        {activeSection === 'insights' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Weekly Business Insights
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Improvements */}
              <Card className="border-t-4 border-t-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-green-700">
                    <TrendingUp className="w-5 h-5" />
                    What Improved
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {weeklyInsights?.improvements?.length > 0 ? (
                    <div className="space-y-2">
                      {weeklyInsights.improvements.map((item, idx) => (
                        <div key={idx} className="p-2 bg-green-50 rounded">
                          <p className="text-sm font-medium">{item.store}</p>
                          <p className="text-xs text-gray-600">{item.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No improvements reported</p>
                  )}
                </CardContent>
              </Card>

              {/* Problems Solved */}
              <Card className="border-t-4 border-t-blue-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-blue-700">
                    <CheckCircle className="w-5 h-5" />
                    Problems Solved
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {weeklyInsights?.problems_solved?.length > 0 ? (
                    <div className="space-y-2">
                      {weeklyInsights.problems_solved.map((item, idx) => (
                        <div key={idx} className="p-2 bg-blue-50 rounded">
                          <p className="text-sm font-medium">{item.store}</p>
                          <p className="text-xs text-gray-600">{item.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No problems resolved</p>
                  )}
                </CardContent>
              </Card>

              {/* Still Stuck */}
              <Card className="border-t-4 border-t-red-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <XCircle className="w-5 h-5" />
                    Still Stuck
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {weeklyInsights?.still_stuck?.length > 0 ? (
                    <div className="space-y-2">
                      {weeklyInsights.still_stuck.map((item, idx) => (
                        <div key={idx} className="p-2 bg-red-50 rounded">
                          <p className="text-sm font-medium">{item.store}</p>
                          <p className="text-xs text-gray-600">{item.message}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No stuck issues</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Silent Stores Warning */}
            {weeklyInsights?.silent_stores?.length > 0 && (
              <Card className="bg-yellow-50 border-l-4 border-l-yellow-500">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                    <span className="font-bold text-yellow-800">Silent Stores (No Updates This Week)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {weeklyInsights.silent_stores.map((store, idx) => (
                      <Badge key={idx} variant="outline" className="bg-white">{store}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-yellow-700 mt-2">These managers haven't submitted updates — follow up required.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Store Card Component (Compact)
function StoreCard({ store }) {
  return (
    <div className={`p-4 rounded-lg border-l-4 ${
      store.staff_kpis?.manager_rating === 'strong' ? 'border-l-green-500 bg-green-50' :
      store.staff_kpis?.manager_rating === 'average' ? 'border-l-yellow-500 bg-yellow-50' :
      'border-l-red-500 bg-red-50'
    }`}>
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-bold">{store.name}</h3>
        <Badge className={
          store.performance?.trend_direction === 'up' ? 'bg-green-500' : 'bg-red-500'
        }>{store.performance?.trend}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-gray-500">Sales/Purchase</p>
          <p className="font-bold">{store.performance?.ratio}%</p>
        </div>
        <div>
          <p className="text-gray-500">Compliance</p>
          <p className="font-bold">{store.compliance?.score}%</p>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t flex justify-between text-xs">
        <span className="text-green-600">{store.staff_kpis?.top_performers} top</span>
        <span className="text-red-600">{store.staff_kpis?.low_performers} low ({store.staff_kpis?.low_performer_pct}%)</span>
      </div>
    </div>
  );
}

// Store Detail Card Component
function StoreDetailCard({ store }) {
  return (
    <Card className={`border-l-4 ${
      store.staff_kpis?.manager_rating === 'strong' ? 'border-l-green-500' :
      store.staff_kpis?.manager_rating === 'average' ? 'border-l-yellow-500' :
      'border-l-red-500'
    }`}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between">
          <span>{store.name}</span>
          <Badge className={
            store.performance?.trend_direction === 'up' ? 'bg-green-500' : 'bg-red-500'
          }>{store.performance?.trend}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Performance */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">PERFORMANCE</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Sales/Purchase</p>
              <p className="text-lg font-bold">{store.performance?.ratio}%</p>
            </div>
            <div className="bg-gray-50 p-2 rounded">
              <p className="text-xs text-gray-500">Stock Movement</p>
              <Badge className={
                store.performance?.stock_movement === 'fast' ? 'bg-green-500' :
                store.performance?.stock_movement === 'normal' ? 'bg-blue-500' :
                store.performance?.stock_movement === 'slow' ? 'bg-yellow-500' : 'bg-red-500'
              }>{store.performance?.stock_movement}</Badge>
            </div>
          </div>
        </div>

        {/* Compliance */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">COMPLIANCE</p>
          <div className="flex items-center justify-between">
            <div>
              <Progress value={store.compliance?.score || 0} className="w-24 h-2" />
            </div>
            <span className="font-bold">{store.compliance?.score}%</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Last audit: {store.compliance?.days_since_audit} days ago
          </p>
        </div>

        {/* Staff KPIs */}
        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">STAFF KPIs</p>
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              <span className="text-green-600 text-sm">{store.staff_kpis?.top_performers} top</span>
              <span className="text-gray-400">|</span>
              <span className="text-yellow-600 text-sm">{store.staff_kpis?.average_performers} avg</span>
              <span className="text-gray-400">|</span>
              <span className="text-red-600 text-sm">{store.staff_kpis?.low_performers} low</span>
            </div>
          </div>
          <div className="mt-2">
            <Badge className={
              store.staff_kpis?.manager_rating === 'strong' ? 'bg-green-100 text-green-700' :
              store.staff_kpis?.manager_rating === 'average' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }>
              Manager: {store.staff_kpis?.manager_rating}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Project Card Component
function ProjectCard({ project, isRisk }) {
  return (
    <Card className={isRisk ? 'border-red-300' : ''}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-bold">{project.name}</h3>
            <p className="text-xs text-gray-500">{project.type}</p>
          </div>
          <Badge className={
            project.status === 'on_track' ? 'bg-green-500' :
            project.status === 'delayed' ? 'bg-yellow-500' :
            project.status === 'at_risk' ? 'bg-orange-500' : 'bg-red-500'
          }>{project.status.replace('_', ' ')}</Badge>
        </div>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span>Completion</span>
              <span>{project.completion_pct}%</span>
            </div>
            <Progress value={project.completion_pct} className="h-2" />
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Budget vs Actual</span>
            <span className={project.budget_vs_actual_pct > 100 ? 'text-red-600 font-bold' : ''}>
              {project.budget_vs_actual_pct}%
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
